import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { App, Project } from '@hangar/shared';
import { toast } from 'sonner';
import { listEnvVars } from '@/api/endpoints';
import { qk, useAppMutations, useApps, useEnvVars, useOidcPolicies, useProfilesOfApps, useProjectMutations } from '@/api/hooks';
import { showMutationError } from '@/lib/mutationError';
import { useCan } from '@/lib/permissions';
import { ConfirmArchiveDialog, Counted, type ImpactItem } from '@/components/ConfirmArchiveDialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { archiveAppsOption, archiveConfirmLabel, archivedToastDescription, sumCounts } from './projectFormModel';

interface Props {
  project: Project | null;
  onOpenChange: (open: boolean) => void;
  onArchived?: (project: Project) => void;
}

interface Impact {
  apps: App[] | undefined;
  appsLoading: boolean;
  profiles: number | undefined;
  vars: number | undefined;
  policies: number | undefined;
  canReadEnv: boolean;
  canAdmin: boolean;
}

/** What archiving touches, counted: apps, build profiles, env vars and CI trust policies (each once it loads). */
function useArchiveImpact(project: Project | null, open: boolean): Impact {
  const canReadEnv = useCan('env:read', project?.id);
  const canAdmin = useCan('admin');
  const appList = useApps(project?.id);
  const appIds = (appList.data ?? []).map((a) => a.id);
  const profiles = useProfilesOfApps(appIds, open);
  const projectVars = useEnvVars({ projectId: project?.id }, open && canReadEnv.allowed);
  const appVars = useQueries({ queries: appIds.map((appId) => ({ queryKey: qk.envVars({ appId }), queryFn: async () => (await listEnvVars({ appId })).data, enabled: open && canReadEnv.allowed })) });
  const policies = useOidcPolicies(open && canAdmin.allowed);
  return {
    apps: appList.data,
    appsLoading: appList.isLoading,
    profiles: sumCounts(profiles.map((q) => q.data?.length)),
    vars: sumCounts([projectVars.data?.length, ...appVars.map((q) => q.data?.length)]),
    policies: policies.data?.filter((p) => p.projectId === project?.id).length,
    canReadEnv: canReadEnv.allowed,
    canAdmin: canAdmin.allowed,
  };
}

/** The impact lines; env vars and CI trust policies only for who may read them. */
function projectArchiveImpact({ apps, profiles, vars, policies, canReadEnv, canAdmin }: Impact): ImpactItem[] {
  const appCount = apps?.length;
  const varsLine = [{ text: <Counted n={vars} one="environment variable" many="environment variables" /> }];
  const policiesLine = [{ text: <><Counted n={policies} one="CI trust policy" many="CI trust policies" />{policies ? ': its pipelines stop getting Hangar tokens' : ''}</> }];
  return [
    {
      text: (
        <>
          <Counted n={appCount} one="app" many="apps" />
          {appCount ? <span className="font-mono text-xs">: {(apps ?? []).map((a) => a.slug).join(', ')}</span> : null}
        </>
      ),
    },
    { text: <Counted n={profiles} one="build profile" many="build profiles" /> },
    ...(canReadEnv ? varsLine : []),
    ...(canAdmin ? policiesLine : []),
    { text: 'Builds, artifacts and env snapshots stay; the slug stays reserved.' },
  ];
}

/** Archiving a project alone does not stop its apps' builds, so the apps are archived first when `withApps`. */
function useArchiveProject(project: Project | null, apps: App[] | undefined, withApps: boolean, onDone: (archived: Project) => void) {
  const projects = useProjectMutations();
  const appMutations = useAppMutations();
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!project) return;
    setBusy(true);
    try {
      if (withApps) for (const app of apps ?? []) await appMutations.update.mutateAsync({ appId: app.id, body: { archived: true }, etag: app.etag });
      const archived = await projects.update.mutateAsync({ projectId: project.id, body: { archived: true }, etag: project.etag });
      toast.success(`${project.name} archived`, { description: archivedToastDescription(withApps, apps?.length) });
      onDone(archived);
    } catch (e) {
      showMutationError(e, 'Could not archive the project');
    } finally {
      setBusy(false);
    }
  };
  return { busy, confirm };
}

/** The option of the same decision: archive the project's apps too (on by default). */
function ArchiveAppsOption({ appCount, checked, onChange }: { appCount: number | undefined; checked: boolean; onChange: (checked: boolean) => void }) {
  if (!appCount) return null;
  return (
    <div className="flex items-start gap-2.5 rounded-lg border px-3 py-2.5">
      <Checkbox id="project-archive-apps" data-testid="project-archive-apps" checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <Label htmlFor="project-archive-apps" className="cursor-pointer flex-col items-start gap-0.5 font-normal leading-snug">
        <span className="text-[13px] font-medium text-foreground">{archiveAppsOption(appCount)}</span>
        <span className="text-xs text-muted-foreground">Recommended: an archived project does not stop the builds of its apps.</span>
      </Label>
    </div>
  );
}

/**
 * Archive a project (`PATCH /projects/{id}` with `archived: true`): design 28's danger zone as a dialog, with the
 * impact summary (apps, build profiles, env vars, CI trust policies) and the slug typed to confirm. Archiving a
 * project alone does not stop its apps' builds (the API checks the app), so the same decision archives the apps
 * first, on by default.
 */
export function ArchiveProjectDialog({ project, onOpenChange, onArchived }: Props) {
  const [withApps, setWithApps] = useState(true);
  const impact = useArchiveImpact(project, Boolean(project));
  const appCount = impact.apps?.length;
  const archive = useArchiveProject(project, impact.apps, withApps, (archived) => {
    onOpenChange(false);
    onArchived?.(archived);
  });
  return (
    <ConfirmArchiveDialog
      open={Boolean(project)}
      onOpenChange={onOpenChange}
      testId="project-archive-dialog"
      title={`Archive ${project?.name ?? 'the project'}?`}
      description="It leaves the project lists and the switcher, and no app can be connected to it. Nothing is deleted."
      impact={projectArchiveImpact(impact)}
      confirmText={project?.slug}
      confirmLabel={archiveConfirmLabel(withApps, appCount)}
      busy={archive.busy || impact.appsLoading}
      onConfirm={() => void archive.confirm()}
    >
      <ArchiveAppsOption appCount={appCount} checked={withApps} onChange={setWithApps} />
    </ConfirmArchiveDialog>
  );
}
